import React from 'react';
import { Briefcase, ChevronRight, LayoutGrid } from 'lucide-react';

export interface ProgramCardData {
  name: string;
  displayName?: string;
  jobCount: number;
}

interface ProgramCardGridProps {
  programs: ProgramCardData[];
  loading: boolean;
  onSelect: (programName: string) => void;
  /** Optional empty-state hint when no programs exist. */
  emptyHint?: string;
}

/**
 * Program-card landing for the job board drill-down.
 *
 * Shows one card per program (from server `/api/jobs/filters` data — never
 * hardcoded) with its live job count. Clicking a card drills into that
 * program's jobs list; the parent renders a back button to return here.
 * Mobile-friendly grid, loading skeletons, and an empty state.
 */
export const ProgramCardGrid: React.FC<ProgramCardGridProps> = ({
  programs,
  loading,
  onSelect,
  emptyHint,
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" role="status" aria-label="Loading programs">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <div className="h-5 w-3/4 applicant-skeleton" />
            <div className="h-4 w-1/2 applicant-skeleton" />
            <div className="h-8 w-full applicant-skeleton" />
          </div>
        ))}
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
        <LayoutGrid className="h-10 w-10 text-slate-300 mx-auto mb-3" aria-hidden="true" />
        <p className="text-sm font-semibold text-slate-700">No programs available right now</p>
        {/* FIX 2026-10-07: helpful empty state explaining when jobs appear */}
        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
          {emptyHint || 'New programs appear here once employers publish jobs. Check back soon, or contact support if you expected to see listings.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {programs.map(p => (
        <button
          key={p.name}
          type="button"
          onClick={() => onSelect(p.name)}
          className="group bg-white rounded-2xl border border-slate-200 p-5 text-left shadow-sm hover:shadow-md hover:border-brand/40 transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          aria-label={`${p.displayName || p.name}, ${p.jobCount} ${p.jobCount === 1 ? 'job' : 'jobs'} — view jobs`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-900 truncate group-hover:text-brand transition-colors">
                {p.displayName || p.name}
              </h3>
              <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-500">
                <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
                {p.jobCount} {p.jobCount === 1 ? 'job' : 'jobs'} available
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-brand group-hover:translate-x-0.5 transition-all shrink-0" aria-hidden="true" />
          </div>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-brand/70 transition-all"
              style={{ width: `${p.jobCount > 0 ? 100 : 0}%` }}
            />
          </div>
        </button>
      ))}
    </div>
  );
};

export default ProgramCardGrid;
